"use client";

import React from "react";

import { UserProps, UserStatus } from "@/types/user";
import { QrStatus } from "@/types/qr";
import { SubscriptionStatus } from "@/types/subscription";
import { UserContextProps } from "./types";
import { PlanProps } from "@/types/plan";
import { BillingData } from "@/containers/MyBillingContainer/components/BillingInformation/types";
import { getTokenData } from "@/services/CookiesService";
import getProfileHandler from "@api/getProfile";
import { useApi } from "@/hooks/api/useApi";
import { userProfileApiToDataTransformer } from "@/services/UserService";
import { GetProfileResponse } from "@/interface/api/getProfile/types";
import { GetBillingPaymentsResponse } from "@/interface/api/getBillingPayments/types";

export const USER_INITIAL_DATA: UserProps = {
  accountId: "",
  userId: undefined,
  firstName: "",
  lastName: "",
  phone: "",
  userStatus: UserStatus.REGISTERED,
  qrStatus: QrStatus.DRAFT,
  selectedSubscriptionId: "",
  email: "",
  freeTrialEndsAt: null,
  freeTrialStartsAt: null,
  subscription: {
    id: "",
    status: SubscriptionStatus.REGISTERED,
    subscribed: false,
    plan: undefined,
  },
};

export const USER_BILLING_INITIAL_DATA: BillingData = {
  companyName: "",
  address: "",
  city: "",
  country: "",
  createdAt: "",
  firstName: "",
  id: "",
  lastName: "",
  phone: "",
  taxId: "",
  updatedAt: "",
  zipCode: "",
  legalStatus: "company",
};

const USER_TRANSACTION_INITIAL_DATA: GetBillingPaymentsResponse = {
  items: [],
  total: 0,
};

let UserContext: React.Context<UserContextProps>;
const { Provider } = (UserContext = React.createContext<UserContextProps>(undefined));

const UserProvider = ({ children }: any) => {
  const [prices, setPrices] = React.useState<PlanProps[]>([]);
  const [userData, setUserDataState] = React.useState<UserProps>(USER_INITIAL_DATA);

  const { request: requestProfile } = useApi(getProfileHandler.getProfile);
  const [userBillingData, setUserBillingDataState] =
    React.useState<BillingData>(USER_BILLING_INITIAL_DATA);

  const [userTransactionData, setUserTransactionData] = React.useState<GetBillingPaymentsResponse>(
    USER_TRANSACTION_INITIAL_DATA
  );

  const setUserData = (data: UserProps) => {
    setUserDataState(data);
  };

  React.useEffect(() => {
    if (userData.userId !== undefined) {
      return;
    }

    const tokenData = getTokenData();
    if (tokenData && !tokenData.isTokenExpired) {
      const fetchUserData = async () => {
        try {
          const response = await requestProfile({
            authorization: tokenData?.token,
          });
          const profileData = response.data as GetProfileResponse;
          const formattedUser = userProfileApiToDataTransformer(profileData);
          setUserDataState(formattedUser);
        } catch {
          setUserDataState({
            ...USER_INITIAL_DATA,
            userId: "",
          });
        }
      };
      fetchUserData();
    } else {
      setUserDataState({
        ...USER_INITIAL_DATA,
        userId: "",
      });
    }
  }, []);

  return (
    <Provider
      value={{
        user: userData,
        setUser: setUserData,
        prices,
        setPrices,
        userBillingData: userBillingData,
        setUserBillingData: setUserBillingDataState,
        userTransactionData,
        setUserTransactionData,
      }}
    >
      {children}
    </Provider>
  );
};

export { UserContext, UserProvider };
